/**
 * L'application : qui est là, où l'on est, et ce qu'on montre.
 *
 * **L'identité est demandée une fois, au démarrage.** `/auth/me` répond 401
 * quand la session a expiré, et l'écran de connexion prend alors la place —
 * partout, y compris sur un lien profond, dont le chemin est conservé pour y
 * revenir après.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { api, ErreurApi } from "./api/client";
import type { Identite } from "./api/types";
import { Chargement } from "./composants/etats";
import { initiales } from "./composants/formats";
import { Accueil } from "./pages/Accueil";
import { Administration } from "./pages/Administration";
import { Affaire } from "./pages/Affaire";
import { Affaires } from "./pages/Affaires";
import { Capacites } from "./pages/Capacites";
import { Traitements } from "./pages/Traitements";
import { Profil } from "./pages/Profil";
import { Profils } from "./pages/Profils";
import { Connexion } from "./pages/Connexion";
import { NouvelleAffaire } from "./pages/NouvelleAffaire";
import { Lien, naviguer, useChemin } from "./routeur";

export function App() {
  const chemin = useChemin();
  const [identite, poserIdentite] = useState<Identite | null>(null);
  const [etat, poserEtat] = useState<"charge" | "connecte" | "dehors">("charge");

  const verifier = useCallback(async () => {
    try {
      const moi = await api.lire<{ donnees: Identite }>("/auth/me");
      poserIdentite(moi.donnees);
      poserEtat("connecte");
    } catch (exc) {
      if (exc instanceof ErreurApi && exc.nonAuthentifie) poserEtat("dehors");
      else poserEtat("dehors");
    }
  }, []);

  useEffect(() => {
    void verifier();
  }, [verifier]);

  if (etat === "charge") {
    return <div className="corps"><div className="contenu"><Chargement lignes={2} /></div></div>;
  }
  if (etat === "dehors") {
    return (
      <Connexion
        motif={
          chemin !== "/" && chemin !== "/connexion"
            ? "Cette page demande une session ouverte."
            : undefined
        }
      />
    );
  }

  return (
    <div className="charpente">
      <header className="barre">
        <Lien vers="/affaires" className="marque">
          <span className="marque__jeton" aria-hidden="true">M</span>
          <span className="marque__nom">MétréOps</span>
        </Lien>

        <Recherche />

        <div className="barre__compte">
          <span className="pastille" aria-hidden="true">
            {initiales(identite?.nom || identite?.courriel)}
          </span>
          <span className="barre__nom">
            {identite?.nom || identite?.courriel || "Compte"}
          </span>
          <Deconnexion
            fait={() => {
              poserIdentite(null);
              poserEtat("dehors");
              naviguer("/");
            }}
          />
        </div>
      </header>

      <div className="charpente__corps">
        <Rail chemin={chemin} role={identite?.platform_role} />
        <main className="corps">
          <Ecran chemin={chemin}
                 assistantNatif={identite?.assistant_natif !== false}
                 moiId={identite?.user_id}
                 role={identite?.platform_role} />
        </main>
      </div>
    </div>
  );
}

/** Le bouton de déconnexion.
 *
 * **Un bouton qui poste, non un lien.** C'était un `<a href="/auth/logout">`,
 * donc un `GET`, et la route est en `POST` : le navigateur recevait un 405 et
 * l'écran affichait `{"detail":"Method Not Allowed"}`.
 *
 * Le verbe ne se change pas pour arranger le lien. Une déconnexion atteignable
 * en `GET` se déclenche depuis n'importe quelle page — une balise `<img>`
 * suffit — et déconnecter quelqu'un à son insu est une nuisance gratuite. Le
 * portail refuse d'ailleurs les écritures à cookie venues d'une autre origine
 * (P17.2) : c'est ce contrôle-là qui protège, et il ne porte que sur les
 * écritures.
 *
 * L'état local est remis **après** que le serveur a répondu : effacer l'écran
 * d'abord montrerait quelqu'un de déconnecté alors que sa session vit encore.
 * Si le portail ne répond pas, on retombe malgré tout sur l'écran de connexion
 * — le cookie est peut-être parti, et laisser l'interface prétendre le
 * contraire serait pire. */
function Deconnexion({ fait }: { fait: () => void }) {
  const [envoi, poserEnvoi] = useState(false);

  return (
    <button
      type="button"
      className="bouton bouton--discret"
      disabled={envoi}
      onClick={async () => {
        poserEnvoi(true);
        try {
          await api.ecrire("/auth/logout");
        } catch {
          /* Le cookie est peut-être déjà tombé : on sort quand même. */
        } finally {
          poserEnvoi(false);
          fait();
        }
      }}
    >
      {envoi ? "Déconnexion…" : "Se déconnecter"}
    </button>
  );
}

/** La barre de recherche de la maquette.
 *
 * **Elle navigue, elle ne cherche pas.** Aucun point d'accès ne fait de
 * recherche globale ; en poser une qui ne rend rien serait pire que de ne rien
 * poser. Ce qu'elle sait faire, elle le fait vraiment : une référence
 * d'affaire saisie mène à la liste filtrée. Le reste attend une API.
 *
 * `⌘K` est annoncé parce qu'il fonctionne — sinon le repère mentirait. */
function Recherche() {
  const champ = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const raccourci = (evenement: KeyboardEvent) => {
      if ((evenement.metaKey || evenement.ctrlKey) && evenement.key === "k") {
        evenement.preventDefault();
        champ.current?.focus();
      }
    };
    window.addEventListener("keydown", raccourci);
    return () => window.removeEventListener("keydown", raccourci);
  }, []);

  return (
    <form
      className="recherche"
      onSubmit={(evenement) => {
        evenement.preventDefault();
        const terme = champ.current?.value.trim() ?? "";
        naviguer(terme ? `/affaires?q=${encodeURIComponent(terme)}` : "/affaires");
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none"
           stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
      <input
        ref={champ}
        type="search"
        placeholder="Rechercher une affaire par référence ou intitulé…"
        aria-label="Rechercher une affaire"
      />
      <kbd>⌘K</kbd>
    </form>
  );
}

/** Le rail de navigation, tel que la maquette le pose.
 *
 * **Il ne porte que des destinations qui existent.** La référence en dessine
 * trois ; deux d'entre elles — l'administration du locataire et l'inventaire du
 * design system — n'ont aujourd'hui aucun écran derrière elles. Les afficher
 * grisées donnerait une interface qui promet ce que la plateforme ne tient pas.
 *
 * Réductible, et le choix **se retient** : un métreur qui travaille sur un
 * portable le replie une fois, pas à chaque ouverture. */
function Rail({ chemin, role }: { chemin: string; role?: string }) {
  const admin = role === "TENANT_ADMIN" || role === "PLATFORM_ADMIN";
  const [replie, poserReplie] = useState(() => {
    try {
      return window.localStorage.getItem("mo.rail") === "replie";
    } catch {
      // Navigation privée, stockage refusé : le rail s'ouvre, et c'est tout.
      return false;
    }
  });

  const basculer = () => {
    poserReplie((avant) => {
      const apres = !avant;
      try {
        window.localStorage.setItem("mo.rail", apres ? "replie" : "ouvert");
      } catch {
        /* rien à faire : le pli vaut pour cette session seulement */
      }
      return apres;
    });
  };

  return (
    <nav className={`rail${replie ? " rail--replie" : ""}`} aria-label="Sections">
      <div className="rail__titre">Espace de travail</div>
      {/* **L'accueil d'abord.** La question qu'on se pose en ouvrant n'est pas
          « quelles affaires ? » mais « laquelle demande une action ? » — le
          tableau de bord y répond et route vers le geste à faire. */}
      <Lien
        vers="/accueil"
        className={`rail__item${chemin === "/" || chemin === "" || chemin.startsWith("/accueil") ? " rail__item--actif" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <path d="M3 11.5 12 4l9 7.5" />
          <path d="M5 10v9h5v-6h4v6h5v-9" />
        </svg>
        <span className="rail__libelle">Accueil</span>
      </Lien>
      <Lien
        vers="/profils"
        className={`rail__item${chemin.startsWith("/profils") ? " rail__item--actif" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <path d="M4 20v-1a5 5 0 0 1 5-5h1" />
          <circle cx="9.5" cy="8" r="3.2" />
          <path d="M15 12a3 3 0 1 0 0-6" />
          <path d="M16 14h1a4 4 0 0 1 4 4v2" />
        </svg>
        <span className="rail__libelle">Profils</span>
      </Lien>
      <Lien
        vers="/affaires"
        className={`rail__item${chemin.startsWith("/affaires") ? " rail__item--actif" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <path d="M3 7h6l2 2h10v10H3z" />
          <path d="M3 7V5h6l2 2" />
        </svg>
        <span className="rail__libelle">Affaires</span>
      </Lien>

      {/* **Le suivi des traitements, juste après les affaires.** Les imports,
          préparations, audits et glossaires tournent en fond, à travers toutes
          les affaires ; ce guichet les réunit — surtout ce qui a échoué. */}
      <Lien
        vers="/traitements"
        className={`rail__item${chemin.startsWith("/traitements") ? " rail__item--actif" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <path d="M12 3a9 9 0 1 0 9 9" />
          <path d="M12 7v5l3 2" />
        </svg>
        <span className="rail__libelle">Traitements</span>
      </Lien>

      {/* **Après les affaires, et non avant.** Le rail sert d'abord à
          travailler ; la vitrine se montre, elle ne s'ouvre pas dix fois par
          jour. La mettre en tête déplacerait le premier clic du métreur. */}
      <Lien
        vers="/capacites"
        className={`rail__item${chemin.startsWith("/capacites") ? " rail__item--actif" : ""}`}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <circle cx="12" cy="12" r="3.2" />
          <circle cx="5" cy="6" r="2.2" />
          <circle cx="19" cy="6" r="2.2" />
          <circle cx="5" cy="18" r="2.2" />
          <circle cx="19" cy="18" r="2.2" />
          <path d="M6.9 7.3 9.8 10M17.1 7.3 14.2 10M6.9 16.7 9.8 14M17.1 16.7 14.2 14" />
        </svg>
        <span className="rail__libelle">Capacités</span>
      </Lien>

      {/* **L'administration, en dernier, et seulement pour qui l'exerce.** Le
          rail ne montre que des destinations réelles ET permises ; un membre
          ordinaire n'a pas d'écran derrière — on ne l'affiche donc pas. */}
      {admin && (
        <Lien
          vers="/administration"
          className={`rail__item${chemin.startsWith("/administration") ? " rail__item--actif" : ""}`}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
               stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
            <path d="M12 3 4 6v6c0 4 3.4 7 8 9 4.6-2 8-5 8-9V6z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
          <span className="rail__libelle">Administration</span>
        </Lien>
      )}

      <button type="button" className="rail__pli" onClick={basculer}
              aria-expanded={!replie}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="1.9" aria-hidden="true">
          <path d={replie ? "m9 6 6 6-6 6" : "m15 6-6 6 6 6"} />
        </svg>
        <span className="rail__libelle">Réduire</span>
      </button>
    </nav>
  );
}

function Ecran({
  chemin,
  assistantNatif,
  moiId,
  role,
}: {
  chemin: string;
  assistantNatif: boolean;
  moiId?: string;
  role?: string;
}) {
  if (chemin === "/" || chemin === "" || chemin === "/accueil") return <Accueil />;
  if (chemin === "/affaires") return <Affaires />;
  if (chemin === "/affaires/nouvelle") return <NouvelleAffaire />;
  if (chemin === "/traitements") return <Traitements />;
  if (chemin === "/capacites") return <Capacites />;
  if (chemin === "/administration") return <Administration moiId={moiId} role={role} />;
  if (chemin === "/profils") return <Profils />;

  const profil = chemin.match(/^\/profils\/([0-9a-f-]{36})$/i);
  if (profil) {
    return <Profil profilId={profil[1]!} />;
  }

  const affaire = chemin.match(/^\/affaires\/([0-9a-f-]{36})$/i);
  if (affaire) {
    return <Affaire affaireId={affaire[1]!} assistantNatif={assistantNatif} />;
  }

  return (
    <div className="contenu">
      <div className="carte">
        <div className="carte__corps pile" style={{ textAlign: "center" }}>
          <h1>Page introuvable</h1>
          <span className="discret">
            L'adresse <span className="mono">{chemin}</span> ne correspond à
            aucun écran.
          </span>
          <div>
            <Lien vers="/affaires" className="bouton bouton--primaire">
              Revenir aux affaires
            </Lien>
          </div>
        </div>
      </div>
    </div>
  );
}
